import { useCallback, useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useBulkPrcPricesMutation,
  useSavePrcPricesMutation,
  type PrcVersion,
  type PrcVersionPayload,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  NBSP,
  OIL_KINDS,
  ScrollRegion,
  VisuallyHidden,
  blockNumber,
  fleetType,
  formatCount,
  normalizeDecimal,
  typo,
} from '@entities/fleet';
import {
  PRC_PRICE_CATEGORIES,
  buildPriceLayout,
  laterYears,
  priceChain,
  recalcSummary,
  roundDecimal,
  shiftDecimal,
  type PriceRowLayout,
  type PriceSectionLayout,
  type PrcPriceCategoryCode,
  type RecalcSummary,
} from '@entities/prc';
import { Notice } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import { POSITIONS, count, formatNumber, formatPercent } from '../format';
import {
  GridInput,
  ManualMark,
  NumTable,
  RecalcNotice,
  ReadValue,
  Section,
  SectionHead,
  SectionNote,
  SectionTitle,
  Stack,
  editorRows,
} from '../ui';
import { EditorNotices, EditorSaveBar, ReadOnlyNote } from './EditorChrome';
import { useBlockEditor, type BlockEditor } from './useBlockEditor';

const pick = (v: PrcVersion) => v.prices;
const layoutOf = (v: PrcVersion) => buildPriceLayout(v.prices, v.params.years, v.params.base_year);

const OIL_LABEL: ReadonlyMap<string, string> = new Map(OIL_KINDS.map((k) => [k.key, k.label]));

function itemLabel(category: string, key: string): string {
  return category === 'lubricants' ? (OIL_LABEL.get(key) ?? key) : key;
}

/** Колонки лет одной ширины во всех категориях — таблицы читаются как один лист. */
const PriceTable = styled(NumTable)`
  ${editorRows}
  table-layout: fixed;
  min-width: 760px;

  thead th:first-child {
    width: 24%;
  }

  tbody th {
    overflow-wrap: anywhere;
  }

  td.cell {
    min-width: 112px;
  }

  tr.index > th,
  tr.index > td {
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.index > th {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  .indexed {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const BulkForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: ${fleetType.body};

  & > label {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & input {
    width: 88px;
    min-width: 0;
  }
`;

const Legend = styled.p`
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  font-size: ${fleetType.small};
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

function categoryInfo(code: PrcPriceCategoryCode) {
  return PRC_PRICE_CATEGORIES.find((c) => c.code === code);
}

/** «+N % на категорию»: база и ручные цены лет × (1 + N / 100), сразу на сервере. */
function BulkControl({
  version,
  variantId,
  category,
  items,
  disabledReason,
  onDone,
}: {
  version: PrcVersion;
  /** Вариант условий: «+N %» — его свой раздел; нет — основной расчёт. */
  variantId?: string;
  category: PrcPriceCategoryCode;
  items: number;
  disabledReason: string | null;
  onDone: (lead: string, res: PrcVersionPayload) => void;
}) {
  const uid = useId();
  const [value, setValue] = useState('5');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bulk, { isLoading }] = useBulkPrcPricesMutation();
  const percent = normalizeDecimal(value);
  const title = categoryInfo(category)?.title ?? category;

  async function run(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (percent === null) {
      setError('Нужно число процентов, например 5 или −3');
      return;
    }
    if (!confirming) {
      setConfirming(true);
      return;
    }
    try {
      const res = await bulk({
        id: version.id,
        category,
        value: percent,
        expected_updated_at: version.updated_at,
        variant_id: variantId,
      }).unwrap();
      setConfirming(false);
      const changed = res.bulk?.items ?? items;
      onDone(
        `${title}: цены изменены на ${formatPercent(percent, 4, true)} — ${count(changed, POSITIONS)}`,
        res,
      );
    } catch (err) {
      setConfirming(false);
      setError(apiErrorText(err, 'Не удалось изменить цены категории.'));
    }
  }

  return (
    <BulkForm onSubmit={(e) => void run(e)} noValidate aria-label={`Изменить все цены: ${title}`}>
      <label htmlFor={`${uid}-pct`}>Все цены категории</label>
      <GridInput
        id={`${uid}-pct`}
        value={value}
        inputMode="decimal"
        autoComplete="off"
        disabled={disabledReason !== null || isLoading}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${uid}-err` : undefined}
        onChange={(e) => {
          setValue(e.target.value);
          setConfirming(false);
        }}
      />
      <span aria-hidden="true">%</span>
      {confirming ? (
        <>
          <span role="status">
            {typo(
              `Изменить ${count(items, POSITIONS)} на ${formatPercent(percent ?? '0', 4, true)}?`,
            )}
          </span>
          <Button type="submit" $variant="primary" $size="sm" disabled={isLoading}>
            {isLoading ? 'Меняю…' : 'Да, изменить'}
          </Button>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={isLoading}
            onClick={() => setConfirming(false)}
          >
            Отмена
          </Button>
        </>
      ) : (
        <Button
          type="submit"
          $variant="secondary"
          $size="sm"
          disabled={disabledReason !== null || isLoading || items === 0}
          title={disabledReason ?? 'База и ручные цены всех позиций × (1 + N / 100)'}
        >
          Применить
        </Button>
      )}
      {error && (
        <span id={`${uid}-err`} role="alert">
          {error}
        </span>
      )}
    </BulkForm>
  );
}

/** Цены позиции по годам из текущих значений формы — подсказка «по индексу». */
function rowChain(
  editor: BlockEditor,
  section: PriceSectionLayout,
  row: PriceRowLayout,
  years: readonly number[],
  baseYear: number,
): Record<number, string> | null {
  const base = normalizeDecimal(editor.values[row.baseCell] ?? '');
  if (base === null) return null;
  const manual: Record<number, string | null> = {};
  const index: Record<number, string | null> = {};
  // Цепочка идёт, пока значения — числа: ошибка ввода в 2028-м не стирает
  // подсказки 2027-го.
  const valid: number[] = [baseYear];
  for (const y of laterYears(years, baseYear)) {
    const m = (editor.values[row.manualCells[y] ?? ''] ?? '').trim();
    const mn = m === '' ? null : normalizeDecimal(m);
    const raw = (editor.values[section.indexCells[y] ?? ''] ?? '').trim();
    const rate = raw === '' ? '0' : normalizeDecimal(raw);
    if ((m !== '' && mn === null) || rate === null) break;
    manual[y] = mn;
    index[y] = shiftDecimal(rate, -2);
    valid.push(y);
  }
  return priceChain(base, manual, index, valid, baseYear);
}

function CellError({ editor, k }: { editor: BlockEditor; k: string }) {
  const err = editor.error(k);
  return err ? <VisuallyHidden id={`${editor.domId(k)}-error`}>{err}</VisuallyHidden> : null;
}

function IndexRow({
  editor,
  section,
  later,
  label,
}: {
  editor: BlockEditor;
  section: PriceSectionLayout;
  later: readonly number[];
  label: string;
}) {
  return (
    <tr className="index">
      <th scope="row" className="sticky">
        Индекс к прошлому году, %
      </th>
      <td className="num muted">—</td>
      {later.map((y) => {
        const key = section.indexCells[y];
        if (!key) return <td key={y} />;
        return (
          <td key={y} className="cell num">
            {editor.editable ? (
              <>
                <GridInput
                  {...editor.bind(key)}
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="0"
                  aria-label={`${label}: индекс ${y} к ${y - 1}, %`}
                />
                <CellError editor={editor} k={key} />
              </>
            ) : (
              <ReadValue>{editor.values[key] ? `${editor.values[key]}${NBSP}%` : '0'}</ReadValue>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function PriceSectionView({
  editor,
  idPrefix,
  section,
  number,
  years,
  baseYear,
  bulk,
}: {
  editor: BlockEditor;
  idPrefix: string;
  section: PriceSectionLayout;
  number: number;
  years: readonly number[];
  baseYear: number;
  bulk: ReactNode;
}) {
  const info = section.code === 'repair' ? null : categoryInfo(section.code);
  const later = laterYears(years, baseYear);
  const title = info?.title ?? 'Ремонты и диагностика';
  return (
    <Section aria-labelledby={`${idPrefix}-${section.code}-h`}>
      <SectionHead>
        <SectionTitle id={`${idPrefix}-${section.code}-h`}>
          <span className="n" aria-hidden="true">
            {blockNumber(number)}
          </span>
          {title}
        </SectionTitle>
        <SectionNote>
          {info
            ? typo(`Цены без НДС, ₽ ${info.unit} · ${count(section.rows.length, POSITIONS)}`)
            : typo('Индекс годовых затрат на ремонт и ТО — цен позиций нет')}
        </SectionNote>
        {bulk && <div className="aside">{bulk}</div>}
      </SectionHead>
      <ScrollRegion label={`Цены: ${title}`}>
        <PriceTable>
          <thead>
            <tr>
              <th scope="col" className="sticky">
                {info?.itemTitle ?? 'Показатель'}
              </th>
              <th scope="col" className="num">
                {baseYear} · база
              </th>
              {later.map((y) => (
                <th key={y} scope="col" className="num">
                  {y}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <IndexRow editor={editor} section={section} later={later} label={title} />
            {section.rows.map((row) => {
              const label = itemLabel(section.code, row.itemKey);
              const chain = rowChain(editor, section, row, years, baseYear);
              return (
                <tr key={row.id} className="row">
                  <th scope="row" className="sticky">
                    {label}
                  </th>
                  <td className="cell num">
                    {editor.editable ? (
                      <>
                        <GridInput
                          {...editor.bind(row.baseCell)}
                          inputMode="decimal"
                          autoComplete="off"
                          spellCheck={false}
                          aria-label={`${label}: цена ${baseYear}, ₽ без НДС`}
                        />
                        <CellError editor={editor} k={row.baseCell} />
                      </>
                    ) : (
                      <ReadValue>
                        {formatNumber(normalizeDecimal(editor.values[row.baseCell] ?? ''), 2, 2)}
                      </ReadValue>
                    )}
                  </td>
                  {later.map((y) => {
                    const key = row.manualCells[y];
                    if (!key) return <td key={y} />;
                    const manual = (editor.values[key] ?? '').trim() !== '';
                    const indexed = chain?.[y] ? roundDecimal(chain[y] ?? '0', 2) : null;
                    const indexedText = indexed ? formatNumber(indexed, 2, 2) : '';
                    return (
                      <td key={y} className="cell num">
                        {editor.editable ? (
                          <>
                            <GridInput
                              {...editor.bind(key)}
                              inputMode="decimal"
                              autoComplete="off"
                              spellCheck={false}
                              placeholder={indexedText}
                              aria-label={
                                manual
                                  ? `${label}: цена ${y}, введена вручную`
                                  : `${label}: цена ${y} по индексу ${indexedText || 'не посчитана'}; введите цену, чтобы задать вручную`
                              }
                            />
                            {manual && <ManualMark>введено вручную</ManualMark>}
                            <CellError editor={editor} k={key} />
                          </>
                        ) : manual ? (
                          <ReadValue>
                            {formatNumber(normalizeDecimal(editor.values[key] ?? ''), 2, 2)}
                            <ManualMark>введено вручную</ManualMark>
                          </ReadValue>
                        ) : (
                          <ReadValue>
                            <span className="indexed">{indexedText || '—'}</span>
                          </ReadValue>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </PriceTable>
      </ScrollRegion>
    </Section>
  );
}

/**
 * Вкладка «Цены»: шины по типоразмерам, топливо, смазочные по видам, гидро- и
 * охлаждающая жидкость и индекс ремонтов. Цена базового года, индексы по
 * годам, ручная цена года важнее индекса. Сохранение пересчитывает версию.
 */
export function PricesTab({
  payload,
  access,
  onDirtyChange,
  onCompare,
}: {
  payload: PrcVersionPayload;
  access: PrcAccess;
  onDirtyChange: (count: number) => void;
  onCompare: () => void;
}) {
  const uid = useId();
  const [savePrices] = useSavePrcPricesMutation();
  const variantId = payload.variant?.id;
  const save = useCallback(
    (args: { id: string; block: PrcVersion['prices']; expected_updated_at: string }) =>
      savePrices({ ...args, variant_id: variantId }).unwrap(),
    [savePrices, variantId],
  );
  const editor = useBlockEditor({
    payload,
    canWrite: access.canWrite,
    pick,
    layout: layoutOf,
    save,
    onDirtyChange,
    idPrefix: uid,
  });
  const [bulkResult, setBulkResult] = useState<{ lead: string; summary: RecalcSummary } | null>(
    null,
  );
  // Одна сводка пересчёта на экране: новая правка заменяет сводку «+N %».
  const lastSaved = editor.recalc;
  useEffect(() => {
    if (lastSaved) setBulkResult(null);
  }, [lastSaved]);
  const { years, base_year: baseYear } = editor.base.params;
  const sections = editor.layout.sections;
  const bulkBlocked = editor.dirtyCount > 0 ? 'Сначала сохраните или отмените правки' : null;

  return (
    <>
      <ReadOnlyNote status={editor.base.status} canWrite={access.canWrite} />
      <EditorNotices editor={editor} lead="Цены сохранены" onCompare={onCompare} />
      {bulkResult && (
        <Stack>
          <RecalcNotice
            summary={bulkResult.summary}
            lead={bulkResult.lead}
            onCompare={onCompare}
            onDismiss={() => setBulkResult(null)}
          />
        </Stack>
      )}
      {sections.length === 0 && (
        <Notice $tone="info">
          <span className="text">В блоке цен нет позиций.</span>
        </Notice>
      )}
      {sections.map((s, i) => (
        <PriceSectionView
          key={s.code}
          editor={editor}
          idPrefix={uid}
          section={s}
          number={i}
          years={years}
          baseYear={baseYear}
          bulk={
            editor.editable && s.code !== 'repair' ? (
              <BulkControl
                version={editor.base}
                variantId={variantId}
                category={s.code}
                items={s.rows.length}
                disabledReason={bulkBlocked}
                onDone={(lead, res) => {
                  editor.dismissRecalc();
                  setBulkResult({ lead, summary: recalcSummary(payload.models, res.models) });
                }}
              />
            ) : null
          }
        />
      ))}
      <Legend>
        {typo(
          `Цена года = цена прошлого года × (1 + индекс года). Пустая ячейка года — цена по индексу, она показана серым. Введённая цена важнее индекса и отмечена «введено вручную»; дальше цепочка идёт от неё. Позиций в блоке: ${formatCount(sections.reduce((n, s) => n + s.rows.length, 0))}.`,
        )}
      </Legend>
      <EditorSaveBar editor={editor} />
    </>
  );
}
